import { useMemo, type CSSProperties } from 'react';
import {
  TRANSLATION_REPLACEMENT_COPY,
  TRANSLATION_STATUS_LABELS,
  getTranslationStatusNote,
  getTranslationReplacementPrompt,
  useTheme,
  type TranslationRecordSubject,
  type TranslationRecordStatus,
} from '@course-authoring/shared-types';

export function TranslationLanguageBanner({
  language,
  flush = false,
  sticky = false,
}: {
  language: string;
  flush?: boolean;
  sticky?: boolean;
}) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);

  return (
    <div
      role="status"
      style={{
        ...styles.languageBanner,
        ...(flush ? { margin: 0 } : {}),
        ...(sticky ? styles.stickyLanguageBanner : {}),
      }}
    >
      <strong>Translation view:</strong>
      <span>{language}</span>
    </div>
  );
}

export function TranslationStatusBadge({
  status,
  showPendingLabel = true,
  missingLabel,
}: {
  status: TranslationRecordStatus;
  showPendingLabel?: boolean;
  /**
   * Overrides the `not-translated` label. Lesson-editor block badges spec
   * `Not Translated` while the metadata/course/map surfaces spec the shared
   * `Not translated` — casing is pinned per surface, independent of
   * `showPendingLabel`.
   */
  missingLabel?: string;
}) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);
  const displayStatus: TranslationRecordStatus = showPendingLabel
    ? status
    : status === 'ht-update-pending'
      ? 'ht'
      : status === 'mt-update-pending' ? 'mt' : status;
  const palette = getStatusPalette(displayStatus);
  const label = status === 'not-translated'
    ? missingLabel ?? (showPendingLabel ? TRANSLATION_STATUS_LABELS[status] : 'Not Translated')
    : showPendingLabel
      ? TRANSLATION_STATUS_LABELS[status]
      : status.startsWith('ht') ? 'HT' : 'MT';

  return (
    <span
      role="status"
      aria-label={`Translation status: ${label}`}
      style={{ ...styles.badge, ...palette }}
    >
      {label}
    </span>
  );
}

export function TranslationStatusNote({
  status,
  subject = 'record',
}: {
  status: TranslationRecordStatus;
  subject?: TranslationRecordSubject;
}) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);
  const note = getTranslationStatusNote(status, subject);

  if (!note) return null;
  const tone = status === 'not-translated' && subject === 'block' ? 'info' : note.tone;

  return (
    <div role="note" style={{ ...styles.notice, ...getNoticePalette(tone) }}>
      {note.text}
    </div>
  );
}

export type TranslationReplacementView = 'prompt' | 'queued' | 'preserved';

export interface TranslationReplacementPromptProps {
  humanTranslationCount: number;
  view: TranslationReplacementView;
  disabled?: boolean;
  onQueue: () => void;
  onPreserve: () => void;
  onChange: () => void;
  subject?: TranslationRecordSubject;
}

export function TranslationReplacementPrompt({
  humanTranslationCount,
  view,
  disabled = false,
  onQueue,
  onPreserve,
  onChange,
  subject = 'record',
}: TranslationReplacementPromptProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);

  if (humanTranslationCount < 1) return null;

  if (view !== 'prompt') {
    const queued = view === 'queued';
    return (
      <div
        role="status"
        style={{ ...styles.notice, ...(queued ? getNoticePalette('warning') : getNoticePalette('info')) }}
      >
        <span>{queued ? TRANSLATION_REPLACEMENT_COPY.queued : TRANSLATION_REPLACEMENT_COPY.preserved}</span>
        <button type="button" onClick={onChange} disabled={disabled} style={styles.linkButton}>
          Change
        </button>
      </div>
    );
  }

  return (
    <div role="group" aria-label="Human translation replacement decision" style={styles.prompt}>
      <strong>{getTranslationReplacementPrompt(humanTranslationCount, subject)}</strong>
      <span style={styles.guidance}>{TRANSLATION_REPLACEMENT_COPY.guidance}</span>
      <div style={styles.actions}>
        <button type="button" onClick={onQueue} disabled={disabled} style={styles.primaryButton}>
          Yes
        </button>
        <button type="button" onClick={onPreserve} disabled={disabled} style={styles.secondaryButton}>
          No
        </button>
      </div>
    </div>
  );
}

function getStatusPalette(status: TranslationRecordStatus): CSSProperties {
  if (status === 'ht-update-pending') {
    return { color: '#92400e', backgroundColor: '#fffbeb', borderColor: '#f59e0b' };
  }
  if (status === 'mt-update-pending') {
    return { color: '#1e40af', backgroundColor: '#eff6ff', borderColor: '#60a5fa' };
  }
  if (status === 'not-translated') {
    return { color: '#791f1f', backgroundColor: '#fcebeb' };
  }
  if (status === 'mt') {
    return { color: '#444441', backgroundColor: '#efefed' };
  }
  return { color: '#166534', backgroundColor: '#f0fdf4', borderColor: '#4ade80' };
}

function getNoticePalette(tone: 'neutral' | 'info' | 'warning'): CSSProperties {
  if (tone === 'warning') {
    return { color: '#78350f', backgroundColor: '#fffbeb', borderColor: '#f59e0b' };
  }
  if (tone === 'info') {
    return { color: '#2a5a8a', backgroundColor: '#eef4fb', borderColor: '#c3d9ef' };
  }
  return { color: '#374151', backgroundColor: '#f9fafb', borderColor: '#d1d5db' };
}

function createStyles(tokens: ReturnType<typeof useTheme>['tokens']): Record<string, CSSProperties> {
  return {
    languageBanner: {
      height: '29px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      margin: `-${tokens.spacing.xl} -${tokens.layout.sectionPaddingX.desktop} ${tokens.spacing.xl}`,
      padding: '7px 24px',
      boxSizing: 'border-box',
      color: '#7a4e00',
      backgroundColor: '#fffbf0',
      borderBottom: '1px solid #f0c040',
      fontSize: '12px',
      lineHeight: 'normal',
    },
    stickyLanguageBanner: {
      position: 'sticky',
      top: 0,
      zIndex: 1,
      flexShrink: 0,
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      alignSelf: 'flex-start',
      padding: '2px 7px',
      border: 0,
      borderRadius: tokens.radius.full,
      fontSize: '10px',
      fontWeight: 600,
      lineHeight: 'normal',
    },
    notice: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: tokens.spacing.md,
      padding: '10px 12px',
      border: '1px solid',
      borderRadius: '6px',
      fontSize: '12px',
      lineHeight: '18px',
    },
    prompt: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
      padding: tokens.spacing.lg,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.surfaceAlt,
      color: tokens.text.primary,
      fontSize: '14px',
      lineHeight: 1.5,
    },
    guidance: { color: tokens.text.muted },
    actions: { display: 'flex', gap: tokens.spacing.sm },
    primaryButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      border: 0,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.ui.fixedAccent,
      color: '#ffffff',
      cursor: 'pointer',
      fontWeight: 600,
    },
    secondaryButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.surface,
      color: tokens.text.primary,
      cursor: 'pointer',
      fontWeight: 600,
    },
    linkButton: {
      padding: 0,
      border: 0,
      background: 'transparent',
      color: tokens.text.link,
      cursor: 'pointer',
      fontWeight: 600,
      textDecoration: 'underline',
    },
  };
}
